/**
 * PATH: packages/ui/src/kanban/components/KanbanCardComposePicker.tsx
 * FILE_ID: UI-KANBAN-CARD-COMPOSE-PICKER-001
 * PURPOSE: Recipient picker for the card-face "Send as email" action — search
 *   org members AND/OR type/tag any email address, then send. On send it hands
 *   the chosen addresses back so the board can open the host composer pre-filled
 *   with the card's title + description (operator reviews, then hits Send).
 * WHY:
 *   FEAT-2026-06-23-KANBAN-CARD-COMPOSE (operator 2026-06-23): "convert this
 *   into an email and send it to someone … search people / tag emails". The
 *   assign picker is member-id only; sending an email needs free email
 *   addresses too, so this is its own focused widget (not PeoplePicker).
 * FLOW:
 *   ┌──────────────┐  pick / type  ┌───────────────┐  Send  ┌──────────────┐
 *   │ search box   │ ───────────▶  │ recipient set │ ─────▶ │ onSend(addrs)│
 *   │ (people|@)   │               │ (chips)       │        │ → composer   │
 *   └──────────────┘               └───────────────┘        └──────────────┘
 * RELATED:
 *   - ../cardCompose: isLikelyEmail / normaliseEmail pure helpers.
 *   - ./KanbanCardQuickActionPickers: renders this inside the portaled popover.
 */
import { useMemo, useState } from 'react'
import { Mail, Plus, Send, X } from 'lucide-react'

import type { KanbanMember } from '../api'
import { isLikelyEmail, normaliseEmail } from '../cardCompose'

interface Recipient {
  email: string
  label: string
}

export interface KanbanCardComposePickerProps {
  members: KanbanMember[]
  /** Fired with the chosen addresses when the operator clicks Send. The
   *  parent closes the popover + opens the host composer. */
  onSend: (recipients: string[]) => void
}

function memberEmail(m: KanbanMember): string | null {
  return m.email ? normaliseEmail(m.email) : null
}

function initials(label: string): string {
  const parts = (label || '?').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase()
  return (label || '?').slice(0, 2).toUpperCase()
}

export function KanbanCardComposePicker({
  members,
  onSend,
}: KanbanCardComposePickerProps) {
  const [query, setQuery] = useState('')
  const [recipients, setRecipients] = useState<Recipient[]>([])

  const chosen = useMemo(
    () => new Set(recipients.map((r) => r.email)),
    [recipients],
  )

  const visibleMembers = useMemo(() => {
    const q = query.trim().toLowerCase()
    return members
      .filter((m) => !!memberEmail(m))
      .filter((m) =>
        q
          ? (m.name || '').toLowerCase().includes(q) ||
            (m.email || '').toLowerCase().includes(q)
          : true,
      )
      .slice(0, 50)
  }, [members, query])

  function add(email: string, label: string) {
    const addr = normaliseEmail(email)
    if (!addr || chosen.has(addr)) return
    setRecipients((prev) => [...prev, { email: addr, label: label || addr }])
    setQuery('')
  }

  function remove(email: string) {
    setRecipients((prev) => prev.filter((r) => r.email !== email))
  }

  const typed = query.trim()
  const canAddTyped = isLikelyEmail(typed) && !chosen.has(normaliseEmail(typed))

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && canAddTyped) {
      e.preventDefault()
      add(typed, typed)
    }
  }

  return (
    <div className="flex max-h-[60vh] flex-col" data-testid="kanban-card-compose-picker">
      <div className="border-b border-neutral-100 px-2.5 py-2 dark:border-zinc-800">
        <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
          <Mail className="h-3.5 w-3.5" aria-hidden="true" /> Send card as email
        </div>
        <input
          autoFocus
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder="Search people or type an email…"
          data-testid="kanban-card-compose-search"
          className="w-full rounded-md border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-sm text-neutral-700 placeholder-neutral-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-zinc-800 dark:bg-zinc-900 dark:text-neutral-200"
        />
        {recipients.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {recipients.map((r) => (
              <span
                key={r.email}
                data-testid={`kanban-card-compose-chip-${r.email}`}
                className="inline-flex items-center gap-1 rounded-full bg-blue-50 py-0.5 pl-2 pr-1 text-[11.5px] font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-200"
                title={r.email}
              >
                <span className="max-w-[140px] truncate">{r.label}</span>
                <button
                  type="button"
                  aria-label={`Remove ${r.label}`}
                  onClick={() => remove(r.email)}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-blue-500 hover:bg-blue-100 hover:text-blue-700 dark:hover:bg-blue-900/60"
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {canAddTyped && (
          <button
            type="button"
            data-testid="kanban-card-compose-add-typed"
            onClick={() => add(typed, typed)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-900"
          >
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1 truncate">
              Add <span className="font-medium">{typed}</span>
            </span>
          </button>
        )}

        {visibleMembers.length === 0 && !canAddTyped && (
          <div className="px-3 py-3 text-[11.5px] text-neutral-500">
            {query.trim()
              ? 'No matching people. Type a full email address to add it.'
              : 'No teammates with an email address.'}
          </div>
        )}

        {visibleMembers.map((m) => {
          const addr = memberEmail(m)!
          const on = chosen.has(addr)
          const label = m.name || m.email || `User ${m.id}`
          return (
            <button
              key={m.id}
              type="button"
              data-testid={`kanban-card-compose-member-${m.id}`}
              onClick={() => (on ? remove(addr) : add(addr, label))}
              aria-pressed={on}
              className={[
                'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-neutral-100 dark:hover:bg-neutral-900',
                on ? 'bg-blue-50/60 dark:bg-blue-950/20' : '',
              ].join(' ')}
            >
              {m.avatar_url ? (
                <img src={m.avatar_url} alt="" className="h-6 w-6 shrink-0 rounded-full object-cover" />
              ) : (
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-neutral-200 text-[9px] font-semibold text-neutral-600 dark:bg-zinc-700 dark:text-neutral-200">
                  {initials(label)}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate">{label}</span>
                {m.email && (
                  <span className="block truncate text-[10.5px] text-neutral-500">{m.email}</span>
                )}
              </span>
              {on && (
                <span className="shrink-0 text-[10px] font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">
                  Added
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div className="border-t border-neutral-100 p-2 dark:border-zinc-800">
        <button
          type="button"
          data-testid="kanban-card-compose-send"
          disabled={recipients.length === 0}
          onClick={() => onSend(recipients.map((r) => r.email))}
          className="flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
          {recipients.length === 0
            ? 'Pick a recipient'
            : `Compose to ${recipients.length} recipient${recipients.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </div>
  )
}
